// SupplierList.js

import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types"; // Import PropTypes
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import api from "../../services/api";
import { useSnackbar } from "context/SnackbarContext";
import { useCompany } from "context/CompanyContext";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import Loader from "components/Loader/Loader";
import { useLanguage } from "context/LanguageContext";
import SupplierEdit from "./SupplierEdit";
import supplierTableData from "./supplierTableData";

// Helper function to get CSRF token from cookies
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const SupplierList = ({ refreshTrigger }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [xmlTypeFilter, setXmlTypeFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const { selectedCompany } = useCompany();
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();

  // Remove these conflicting state variables
  // const [currentPageIndex, setCurrentPageIndex] = useState(0);
  // const [currentPageSize, setCurrentPageSize] = useState(20);

  const fetchSuppliers = useCallback(async () => {
    try {
      if (!selectedCompany) return;

      // Build URL with filters
      const params = new URLSearchParams({
        company_id: selectedCompany.id,
        imported: "True",
      });

      if (xmlTypeFilter) {
        params.append("xml_type", xmlTypeFilter);
      }

      if (searchFilter) {
        params.append("search", searchFilter);
      }

      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/api/suppliers/?${params.toString()}`
      );
      const result = response.data;

      console.log("API Response:", result);

      if (result.success) {
        const suppliersWithPrimary = result.suppliers.map((supplier) => {
          return {
            ...supplier,
            id: supplier.supplier_id, // Naudojame supplier_id vietoje id
            primary_warehouse: supplier.primary_warehouse || "",
            primary_group: supplier.primary_group || "",
            pvm_code: supplier.pvm_code || "",
            xml_type: supplier.xml_type || "",
          };
        });
        setRows(suppliersWithPrimary);
      } else {
        console.error("Error fetching suppliers:", result.error);
        showSnackbar(`Error: ${result.error}`, "error");
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      showSnackbar("Error fetching suppliers", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, xmlTypeFilter, searchFilter, showSnackbar]);

  useEffect(() => {
    console.log(
      "Effect triggered with selectedCompany:",
      selectedCompany,
      "and refreshTrigger:",
      refreshTrigger,
      "xmlTypeFilter:",
      xmlTypeFilter,
      "searchFilter:",
      searchFilter
    );
    if (selectedCompany) {
      setLoading(true);
      fetchSuppliers();
    }
  }, [selectedCompany, refreshTrigger, xmlTypeFilter, searchFilter, fetchSuppliers]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchFilter(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleEdit = useCallback(
    (supplier) => {
      setSelectedSupplier(supplier);
      if (!editModalOpen) {
        setEditModalOpen(true);
      }
    },
    [editModalOpen]
  );

  const handleDelete = useCallback(
    async (supplierId) => {
      if (window.confirm("Ar tikrai norite ištrinti šį tiekėją?")) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/delete/`, {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-CSRFToken": getCookie("csrftoken"),
            },
            body: JSON.stringify({
              supplier_ids: [supplierId],
            }),
          });

          const result = await response.json();
          if (result.success) {
            showSnackbar("Tiekėjas sėkmingai ištrintas", "success");
            fetchSuppliers(); // Refresh the list
          } else {
            showSnackbar(`Klaida: ${result.error}`, "error");
          }
        } catch (error) {
          console.error("Error deleting supplier:", error);
          showSnackbar("Klaida trinant tiekėją", "error");
        }
      }
    },
    [selectedCompany, showSnackbar, fetchSuppliers]
  );

  const handleUpdate = () => {
    fetchSuppliers();
  };

  const columns = useMemo(
    () => supplierTableData(handleEdit, handleDelete).columns,
    [handleEdit, handleDelete]
  );

  const data = useMemo(() => rows, [rows]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { pageIndex: tablePageIndex, pageSize: tablePageSize },
    gotoPage,
    setPageSize,
    pageOptions,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 20 },
      manualPagination: false,
    },
    useSortBy,
    usePagination,
    useRowSelect
  );

  const handleSuppliersPerPageChange = (event) => {
    const newPageSize = Number(event.target.value);
    setPageSize(newPageSize);
    gotoPage(0); // Reset to first page when changing page size
  };

  const handlePageChange = (event, value) => {
    gotoPage(value - 1); // Use React Table's gotoPage instead of local state
  };

  if (loading) return <Loader />;

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              {/* Filter Controls */}
              <MDBox display="flex" gap={2} mb={3} flexWrap="wrap">
                <TextField
                  label={t("searchSuppliers")}
                  variant="outlined"
                  size="small"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  sx={{ minWidth: 200 }}
                  placeholder={t("searchByNameOrRegistration")}
                />
                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>{t("xmlType")}</InputLabel>
                  <Select
                    value={xmlTypeFilter}
                    onChange={(e) => setXmlTypeFilter(e.target.value)}
                    label={t("xmlType")}
                  >
                    <MenuItem value="">{t("all")}</MenuItem>
                    <MenuItem value="quantitative">Quantitative</MenuItem>
                    <MenuItem value="summary">Summary</MenuItem>
                  </Select>
                </FormControl>
              </MDBox>
              {/* eslint-disable react/jsx-props-no-spreading */}
              <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  {headerGroups.map((headerGroup, index) => (
                    <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id || index}>
                      {headerGroup.headers.map((column, colIndex) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          key={column.id || colIndex}
                          style={{
                            borderBottom: "1px solid #ddd",
                            background: "#f4f4f4",
                            padding: "8px",
                            fontWeight: "bold",
                            textAlign: "left",
                            width: `${column.width || "20%"}`,
                          }}
                        >
                          {column.render("Header")}
                          <span>
                            {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row, rowIndex) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id || rowIndex}>
                        {row.cells.map((cell, cellIndex) => (
                          <td
                            {...cell.getCellProps()}
                            key={cell.column.id || cellIndex}
                            style={{
                              padding: "8px",
                              borderBottom: "1px solid #ddd",
                              textAlign: "left",
                              width: `${cell.column.width || "20%"}`,
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* eslint-enable react/jsx-props-no-spreading */}

              <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={3}>
                <MDBox display="flex" alignItems="center" gap={2}>
                  <FormControl variant="outlined" style={{ minWidth: 120 }}>
                    <InputLabel id="suppliers-per-page-label">{t("perPage")}</InputLabel>
                    <Select
                      labelId="suppliers-per-page-label"
                      value={tablePageSize} // Use React Table's pageSize
                      onChange={handleSuppliersPerPageChange}
                      label={t("perPage")}
                    >
                      {[20, 40, 100].map((pageSizeOption) => (
                        <MenuItem key={pageSizeOption} value={pageSizeOption}>
                          {t("showAll")} {pageSizeOption}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    Showing {tablePageIndex * tablePageSize + 1}-
                    {Math.min((tablePageIndex + 1) * tablePageSize, data.length)} of {data.length}{" "}
                    results
                  </span>
                </MDBox>
                <Pagination
                  count={pageOptions.length}
                  page={tablePageIndex + 1} // Use React Table's pageIndex
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                />
              </MDBox>
            </MDBox>

            {selectedSupplier && (
              <SupplierEdit
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                supplier={selectedSupplier}
                onUpdate={handleUpdate}
              />
            )}
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
};

// Define prop types
SupplierList.propTypes = {
  refreshTrigger: PropTypes.number,
};

export default SupplierList;
